import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomModal } from 'components/shared';
import { EditClientBalanceTable } from 'components/Storages/index';
import { usePalletsTypes } from 'constants/palletTypes';
import { useTableForm } from 'hooks';
import { ClientUserDTO, editPalletBalance, PalletBalance } from 'interfaces';
import { Banner } from 'storybook';
import { getKeys } from 'utils/functions';
import { editUserBalance } from 'utils/validation/editUserBalance';

import styles from './EditClientBalanceModal.module.css';

type EditClientBalanceModalProps = {
   onClose: () => void;
   onSubmit: (palletBalances: editPalletBalance[]) => void;
   isOpen: boolean;
   client: ClientUserDTO;
   rentedPalletsBalance: PalletBalance;
   damagedByClientPallets: PalletBalance;
   lostOrDestroyedByClientPallets: PalletBalance;
   damagedOrLostBySupplierPallets: PalletBalance;
};

const EditClientBalanceModal = ({
   onClose,
   onSubmit,
   isOpen,
   client,
   rentedPalletsBalance,
   damagedByClientPallets,
   lostOrDestroyedByClientPallets,
   damagedOrLostBySupplierPallets,
}: EditClientBalanceModalProps) => {
   const { t } = useTranslation();
   const { PALLET_TYPES } = usePalletsTypes();

   const initialState = React.useMemo(
      () =>
         getKeys(PALLET_TYPES).map(palletName => ({
            palletName,
            isAvailableForUser: client.availablePalletTypes.includes(palletName),
            rented: rentedPalletsBalance[palletName]?.toString(),
            lostOrDestroyedByUserFault: lostOrDestroyedByClientPallets[palletName]?.toString(),
            damagedByUserFault: damagedByClientPallets[palletName]?.toString(),
            receivedDamaged: damagedOrLostBySupplierPallets[palletName]?.toString(),
         })),
      [
         PALLET_TYPES,
         client.availablePalletTypes,
         rentedPalletsBalance,
         damagedByClientPallets,
         lostOrDestroyedByClientPallets,
         damagedOrLostBySupplierPallets,
      ],
   );

   const { rows, errors, touched, handleChangeValue, handleSubmitForm, setRows, hasErrors } =
      useTableForm<editPalletBalance>({
         initialState,
         onSubmit: onSubmit,
         validations: editUserBalance,
      });

   const clearRows = useCallback(() => {
      setRows(initialState);
   }, [initialState, setRows]);

   useEffect(() => {
      clearRows();
   }, [
      clearRows,
      rentedPalletsBalance,
      damagedByClientPallets,
      lostOrDestroyedByClientPallets,
      damagedOrLostBySupplierPallets,
   ]);

   return (
      <CustomModal
         size={1264}
         onClose={() => {
            clearRows();
            onClose();
         }}
         opened={isOpen}
         primaryButtonProps={{ text: t('modals.edit_client_balance.submit'), disabled: hasErrors }}
         secondaryButtonProps={{
            text: t('common.cancel'),
            onClick: () => {
               clearRows();
               onClose();
            },
         }}
         title={t('modals.editPallets.title')}
         innerTitle={t('modals.edit_client_balance.subtitle', {
            companyName: client.companyName,
            address: client.address,
            zipCode: client.zipCode,
            city: client.city,
         })}
         onSubmit={handleSubmitForm}
         modalContentClassName={styles.modalContent}
         className={styles.container}
      >
         <EditClientBalanceTable
            rows={rows}
            errors={errors}
            touched={touched}
            handleChangeValue={handleChangeValue}
         />
         {hasErrors && (
            <Banner
               className={styles.errorBanner}
               variant="error"
               children={t('modals.editPallets.errorBanner')}
               fullWidth
            />
         )}
      </CustomModal>
   );
};

export default EditClientBalanceModal;
